import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { listVehicles, type Vehicle } from "../lib/fleet";
import {
  listSchedules, createSchedule, deleteSchedule, addRecord, listRecords, setMeter,
  reportFault, listFaults, resolveFault,
  INTERVAL_TYPES, INTERVAL_LABELS, DUE_LABELS, SEVERITY_LABELS,
  type Schedule, type ServiceRecord, type FaultCode, type IntervalType, type DueStatus,
} from "../lib/maintenance";

const DUE_STYLE: Record<DueStatus, string> = {
  ok: "text-verified", due_soon: "text-[#c9a227]", overdue: "text-danger", unknown: "text-muted",
};
const money = (c: number | null) => (c == null ? "" : `$${(c / 100).toFixed(2)}`);

export function Maintenance() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vid, setVid] = useState<string>("");
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [records, setRecords] = useState<ServiceRecord[]>([]);
  const [faults, setFaults] = useState<FaultCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [sForm, setSForm] = useState({ task: "", interval_type: "miles" as IntervalType, interval_value: "" });
  const [meter, setMeterForm] = useState("");
  const [dForm, setDForm] = useState({ code: "", description: "", severity: 2 });

  useEffect(() => {
    listVehicles().then((r) => { setVehicles(r.vehicles); if (r.vehicles[0]) setVid(r.vehicles[0].id); }).finally(() => setLoading(false));
  }, []);

  async function refresh(id: string) {
    if (!id) return;
    const [s, r, f] = await Promise.all([listSchedules(id), listRecords(id), listFaults(id)]);
    setSchedules(s.schedules); setRecords(r.records); setFaults(f.dtcs);
  }
  useEffect(() => { refresh(vid); }, [vid]);

  async function addSchedule(e: FormEvent) {
    e.preventDefault();
    if (!vid || !sForm.task || !sForm.interval_value) return;
    setBusy(true);
    try {
      await createSchedule({ vehicle_id: vid, task: sForm.task, interval_type: sForm.interval_type, interval_value: Number(sForm.interval_value) });
      setSForm({ task: "", interval_type: "miles", interval_value: "" });
      await refresh(vid);
    } finally { setBusy(false); }
  }

  async function logService(s: Schedule) {
    setBusy(true);
    try { await addRecord({ vehicle_id: vid, schedule_id: s.id, task: s.task }); await refresh(vid); } finally { setBusy(false); }
  }
  async function removeSchedule(id: string) {
    setBusy(true);
    try { await deleteSchedule(id); await refresh(vid); } finally { setBusy(false); }
  }
  async function saveMeter(e: FormEvent) {
    e.preventDefault();
    if (!vid || !meter) return;
    setBusy(true);
    try { await setMeter({ vehicle_id: vid, odometer: Number(meter) }); setMeterForm(""); await refresh(vid); } finally { setBusy(false); }
  }
  async function addFault(e: FormEvent) {
    e.preventDefault();
    if (!vid || !dForm.code) return;
    setBusy(true);
    try {
      await reportFault({ vehicle_id: vid, code: dForm.code, description: dForm.description || undefined, severity: dForm.severity });
      setDForm({ code: "", description: "", severity: 2 });
      await refresh(vid);
    } finally { setBusy(false); }
  }
  async function clearFault(id: string) {
    setBusy(true);
    try { await resolveFault(id); await refresh(vid); } finally { setBusy(false); }
  }

  if (loading) return <p className="mx-auto max-w-3xl font-mono text-sm text-muted">Loading…</p>;

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Maintenance & diagnostics"
        subtitle="Preventive schedules, service history, and fault codes per vehicle. Log odometer to keep mileage-based due dates accurate — no hardware required."
      />

      {vehicles.length === 0 ? (
        <p className="font-mono text-sm text-muted">Add a vehicle in Fleet first, then track its maintenance here.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <select className={inputClass} value={vid} onChange={(e) => setVid(e.target.value)} aria-label="Vehicle" data-testid="vehicle-select">
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.unit_number}{v.make ? ` · ${v.make}` : ""}</option>)}
            </select>
            <form className="flex items-center gap-2" onSubmit={saveMeter}>
              <input className={inputClass} type="number" placeholder="Current odometer" value={meter} onChange={(e) => setMeterForm(e.target.value)} aria-label="Odometer" />
              <Button type="submit" variant="ghost" disabled={busy}>Save meter</Button>
            </form>
          </div>

          <Panel title="Preventive schedules">
            <form className="mb-3 grid gap-2 sm:grid-cols-4" onSubmit={addSchedule}>
              <input className={`${inputClass} sm:col-span-2`} placeholder="Task (e.g. Oil change)" value={sForm.task} onChange={(e) => setSForm({ ...sForm, task: e.target.value })} aria-label="Task" />
              <select className={inputClass} value={sForm.interval_type} onChange={(e) => setSForm({ ...sForm, interval_type: e.target.value as IntervalType })} aria-label="Interval type">
                {INTERVAL_TYPES.map((t) => <option key={t} value={t}>every N {INTERVAL_LABELS[t]}</option>)}
              </select>
              <input className={inputClass} type="number" placeholder="Interval" value={sForm.interval_value} onChange={(e) => setSForm({ ...sForm, interval_value: e.target.value })} aria-label="Interval value" />
              <Button type="submit" disabled={busy} className="sm:col-span-4" data-testid="add-schedule">Add schedule</Button>
            </form>
            {schedules.length === 0 ? (
              <p className="font-mono text-sm text-muted">No schedules yet.</p>
            ) : (
              <ul className="grid gap-2" data-testid="schedule-list">
                {schedules.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                    <span className="grid gap-0.5">
                      <span className="font-bold">{s.task}</span>
                      <span className="font-mono text-[11px] text-muted">
                        every {s.interval_value.toLocaleString()} {INTERVAL_LABELS[s.interval_type]}
                        {s.remaining != null ? ` · ${s.remaining.toLocaleString()} ${INTERVAL_LABELS[s.interval_type]} left` : ""}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className={`font-mono text-[11px] font-bold uppercase ${DUE_STYLE[s.due_status]}`}>{DUE_LABELS[s.due_status]}</span>
                      <button type="button" className="font-mono text-[11px] text-verified hover:underline" disabled={busy} onClick={() => logService(s)}>Log service</button>
                      <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy} onClick={() => removeSchedule(s.id)}>Delete</button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Fault codes (DTC)">
            <form className="mb-3 grid gap-2 sm:grid-cols-4" onSubmit={addFault}>
              <input className={inputClass} placeholder="Code (P0420 / SPN…)" value={dForm.code} onChange={(e) => setDForm({ ...dForm, code: e.target.value })} aria-label="Fault code" />
              <input className={`${inputClass} sm:col-span-2`} placeholder="Description (optional)" value={dForm.description} onChange={(e) => setDForm({ ...dForm, description: e.target.value })} />
              <select className={inputClass} value={dForm.severity} onChange={(e) => setDForm({ ...dForm, severity: Number(e.target.value) })} aria-label="Fault severity">
                {[1, 2, 3].map((n) => <option key={n} value={n}>{SEVERITY_LABELS[n]}</option>)}
              </select>
              <Button type="submit" disabled={busy} className="sm:col-span-4" data-testid="report-fault">Report fault</Button>
            </form>
            {faults.length === 0 ? (
              <p className="font-mono text-sm text-muted">No fault codes.</p>
            ) : (
              <ul className="grid gap-2" data-testid="fault-list">
                {faults.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                    <span className="grid gap-0.5">
                      <span className="font-mono font-bold">{f.code}{f.resolved ? " ✓" : ""}</span>
                      {f.description && <span className="font-mono text-[11px] text-muted">{f.description}</span>}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className={`font-mono text-[11px] font-bold ${f.severity === 3 ? "text-danger" : f.severity === 2 ? "text-[#c9a227]" : "text-muted"}`}>{SEVERITY_LABELS[f.severity]}</span>
                      {!f.resolved && <button type="button" className="font-mono text-[11px] text-verified hover:underline" disabled={busy} onClick={() => clearFault(f.id)}>Resolve</button>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Service history">
            {records.length === 0 ? (
              <p className="font-mono text-sm text-muted">No service logged yet.</p>
            ) : (
              <ul className="grid gap-2" data-testid="record-list">
                {records.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                    <span className="font-bold">{r.task}</span>
                    <span className="font-mono text-[11px] text-muted">
                      {r.performed_at}{r.odometer != null ? ` · ${r.odometer.toLocaleString()} mi` : ""}{r.cost_cents != null ? ` · ${money(r.cost_cents)}` : ""}{r.vendor ? ` · ${r.vendor}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
